import { useCallback, useEffect, useState } from "react";
import client from "@/lib/api";
import { toast } from "sonner";
import {
  Eye, ToggleLeft, ToggleRight, House, FileText, UploadSimple, Trash, DownloadSimple,
} from "@phosphor-icons/react";

const DOC_CATEGORY_FALLBACK = [
  "Lease Agreement", "ID Verification", "Deposit Receipt",
  "Inspection Report", "Utility Bill", "RTB Registration", "Other",
];

const readAsDataURL = (file) =>
  new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });

export default function AdminCustomers() {
  const [customers, setCustomers] = useState([]);
  const [inspections, setInspections] = useState([]);
  const [properties, setProperties] = useState([]);
  const [filter, setFilter] = useState("");
  const [assignFor, setAssignFor] = useState(null);
  const [assignPropFor, setAssignPropFor] = useState(null);
  const [viewing, setViewing] = useState(null);

  const load = useCallback(async () => {
    const [c, i, p] = await Promise.all([
      client.get("/admin/customers"),
      client.get("/inspections"),
      client.get("/admin/properties/with_assignments"),
    ]);
    setCustomers(c.data);
    setInspections(i.data);
    setProperties(p.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const filtered = customers.filter((c) =>
    !filter || c.name?.toLowerCase().includes(filter.toLowerCase()) ||
    c.email?.toLowerCase().includes(filter.toLowerCase()) ||
    (c.phone || "").toLowerCase().includes(filter.toLowerCase())
  );

  const toggleStatus = async (c) => {
    const next = (c.account_status || "active") === "active" ? "inactive" : "active";
    await client.post(`/admin/customers/${c.id}/status`, { status: next });
    toast.success(`Account ${next === "active" ? "activated" : "deactivated"}`);
    load();
  };

  const openView = async (c) => {
    const { data } = await client.get(`/admin/customers/${c.id}`);
    setViewing(data);
  };

  return (
    <div data-testid="admin-customers-page">
      <div className="text-[11px] uppercase tracking-[0.3em] text-[#64748B] mb-1">Admin · Customers</div>
      <h1 className="trent-heading text-3xl md:text-4xl font-black tracking-tighter text-[#0F172A] mb-6">
        Customer management
      </h1>

      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search by name, email or phone…"
        className="trent-input mb-4 max-w-md"
        data-testid="customer-search"
      />

      <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
            <tr>
              <th className="text-left px-4 py-3">Customer</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Email</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Phone</th>
              <th className="text-left px-4 py-3 hidden lg:table-cell">Signup date</th>
              <th className="text-left px-4 py-3">Status</th>
              <th className="text-left px-4 py-3">Reports</th>
              <th className="px-4 py-3 w-48 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={7} className="text-center text-[#64748B] py-10">No customer accounts.</td></tr>
            )}
            {filtered.map((c) => {
              const status = c.account_status || "active";
              return (
                <tr key={c.id} className="border-t border-[#E5E7EB]" data-testid={`customer-row-${c.id}`}>
                  <td className="px-4 py-3 font-semibold">{c.name}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#475569]">{c.email}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#475569]">{c.phone || "—"}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-[#64748B] text-xs">{c.created_at?.slice(0, 10)}</td>
                  <td className="px-4 py-3">
                    {status === "active" ? (
                      <span className="condition-pill bg-[#DCFCE7] text-[#14532D] border-[#86EFAC]">Active</span>
                    ) : (
                      <span className="condition-pill bg-[#FEE2E2] text-[#991B1B] border-[#F87171]">Inactive</span>
                    )}
                    {c.deletion_requested && (
                      <span className="condition-pill ml-1 bg-[#FEF3C7] text-[#92400E] border-[#FBBF24]">Deletion req.</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-[#475569]">{c.assigned_count}</td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex gap-1">
                      <button onClick={() => openView(c)} className="p-2 hover:bg-[#F1F5F9] rounded-sm" title="View / Edit" data-testid={`view-customer-${c.id}`}>
                        <Eye size={16} />
                      </button>
                      <button onClick={() => setAssignPropFor(c)} className="p-2 hover:bg-[#F1F5F9] rounded-sm" title="Assign properties" data-testid={`assign-properties-${c.id}`}>
                        <House size={16} weight="duotone" className="text-[#1E3A5F]" />
                      </button>
                      <button onClick={() => toggleStatus(c)} className="p-2 hover:bg-[#F1F5F9] rounded-sm" title={status === "active" ? "Deactivate" : "Activate"} data-testid={`toggle-status-${c.id}`}>
                        {status === "active"
                          ? <ToggleRight size={18} weight="duotone" className="text-[#16A34A]" />
                          : <ToggleLeft size={18} weight="duotone" className="text-[#991B1B]" />}
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-10">
        <h2 className="trent-heading text-xl font-bold text-[#0F172A] mb-3">Assign customers to inspections</h2>
        <p className="text-sm text-[#475569] mb-4">
          Pick an inspection then toggle the customers who should be able to see it from their portal.
        </p>
        <div className="bg-white border border-[#E5E7EB] rounded-md overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#F1F5F9] text-[10px] uppercase tracking-[0.2em] text-[#64748B]">
              <tr>
                <th className="text-left px-4 py-3">Inspection</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Type</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Date</th>
                <th className="text-left px-4 py-3">Assigned</th>
                <th className="px-4 py-3 w-32 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {inspections.map((i) => (
                <tr key={i.id} className="border-t border-[#E5E7EB]" data-testid={`assign-row-${i.id}`}>
                  <td className="px-4 py-3 font-semibold">{i.id.slice(0, 8)}…</td>
                  <td className="px-4 py-3 hidden md:table-cell">{i.inspection_type}</td>
                  <td className="px-4 py-3 hidden md:table-cell text-[#475569]">{i.inspection_date}</td>
                  <td className="px-4 py-3 text-[#475569]">{(i.customer_user_ids || []).length}</td>
                  <td className="px-4 py-3 text-right">
                    <button onClick={() => setAssignFor(i)} className="trent-btn-secondary py-1 px-3" data-testid={`assign-btn-${i.id}`}>
                      Assign
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {assignFor && (
        <AssignInspectionModal inspection={assignFor} customers={customers} onClose={() => { setAssignFor(null); load(); }} />
      )}
      {assignPropFor && (
        <AssignPropertiesModal
          customer={assignPropFor}
          properties={properties}
          onClose={() => { setAssignPropFor(null); load(); }}
        />
      )}
      {viewing && (
        <ViewCustomerModal
          customer={viewing}
          onClose={() => setViewing(null)}
          refresh={async () => {
            const { data } = await client.get(`/admin/customers/${viewing.id}`);
            setViewing(data);
            load();
          }}
        />
      )}
    </div>
  );
}

function ViewCustomerModal({ customer, onClose, refresh }) {
  const [tab, setTab] = useState("profile");
  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="view-customer-modal">
      <div className="bg-white rounded-md w-full max-w-3xl max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b border-[#E5E7EB] px-6 pt-6 pb-3 z-10">
          <div className="flex items-start justify-between mb-3">
            <div>
              <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] font-semibold">Customer profile</div>
              <h3 className="trent-heading text-2xl font-black tracking-tighter text-[#0F172A] mt-1">{customer.name}</h3>
              <div className="text-xs text-[#64748B] mt-1">{customer.email}</div>
            </div>
            <button onClick={onClose} className="text-[#64748B] hover:text-[#0F172A] text-lg" data-testid="close-view-modal">✕</button>
          </div>
          <div className="flex gap-1 -mb-3">
            {[
              { key: "profile", label: "Profile" },
              { key: "properties", label: `Properties (${(customer.assigned_properties || []).length})` },
              { key: "reports", label: `Reports (${(customer.inspections || []).length})` },
              { key: "documents", label: `Documents (${customer.documents_count || 0})` },
            ].map((t) => (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                data-testid={`tab-${t.key}`}
                className={`text-xs uppercase tracking-wider font-semibold px-3 py-2 border-b-2 ${
                  tab === t.key
                    ? "border-[#16A34A] text-[#0F172A]"
                    : "border-transparent text-[#64748B] hover:text-[#0F172A]"
                }`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="p-6">
          {tab === "profile" && <ProfileTab customer={customer} />}
          {tab === "properties" && <PropertiesTab customer={customer} />}
          {tab === "reports" && <ReportsTab customer={customer} />}
          {tab === "documents" && <DocumentsTab customer={customer} refresh={refresh} />}
        </div>
      </div>
    </div>
  );
}

function ProfileTab({ customer }) {
  return (
    <div className="grid grid-cols-2 gap-3 text-sm">
      <Field label="Email" value={customer.email} />
      <Field label="Phone" value={customer.phone || "—"} />
      <Field label="Signup date" value={customer.created_at?.slice(0, 10)} />
      <Field label="Status" value={(customer.account_status || "active") === "active" ? "Active" : "Inactive"} />
      <Field label="Plan" value={customer.plan} />
      <Field label="GDPR" value={customer.deletion_requested ? "Deletion requested" : "Active consent"} />
    </div>
  );
}

function PropertiesTab({ customer }) {
  const props = customer.assigned_properties || [];
  if (props.length === 0) {
    return (
      <div className="text-sm text-[#64748B] border border-dashed border-[#E5E7EB] rounded-md p-6 text-center" data-testid="empty-properties">
        No properties assigned yet. Close this modal and use the
        <span className="inline-flex items-center gap-1 mx-1 font-semibold text-[#0F172A]">
          <House size={12} /> property
        </span>
        icon on the row to assign properties to this customer.
      </div>
    );
  }
  return (
    <div className="border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]">
      {props.map((p) => (
        <div key={p.id} className="p-3 text-sm flex items-center justify-between">
          <div>
            <div className="font-semibold">{p.address}</div>
            <div className="text-xs text-[#64748B]">{p.eircode || "—"} · {p.property_type}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function ReportsTab({ customer }) {
  const reports = customer.inspections || [];
  if (reports.length === 0) {
    return (
      <div className="text-sm text-[#64748B] border border-dashed border-[#E5E7EB] rounded-md p-6 text-center">
        No inspections assigned yet. Use the “Assign customers to inspections” table below the customer list.
      </div>
    );
  }
  return (
    <div className="border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]">
      {reports.map((i) => (
        <div key={i.id} className="p-3 text-sm flex items-center justify-between">
          <div>
            <div className="font-semibold">{i.property_address || i.id.slice(0, 8)}</div>
            <div className="text-xs text-[#64748B]">{i.inspection_type} · {i.inspection_date} · {i.status}</div>
          </div>
        </div>
      ))}
    </div>
  );
}

function DocumentsTab({ customer, refresh }) {
  const [docs, setDocs] = useState([]);
  const [categories, setCategories] = useState(DOC_CATEGORY_FALLBACK);
  const [category, setCategory] = useState("Lease Agreement");
  const [label, setLabel] = useState("");
  const [propertyId, setPropertyId] = useState("");
  const [uploading, setUploading] = useState(false);

  const customerProps = customer.assigned_properties || [];

  const load = useCallback(async () => {
    const { data } = await client.get(`/admin/customers/${customer.id}/documents`);
    setDocs(data);
  }, [customer.id]);

  useEffect(() => {
    load();
    client.get("/admin/document_categories").then((r) => setCategories(r.data)).catch(() => {});
  }, [load]);

  const onUpload = async (files) => {
    if (!files || files.length === 0) return;
    setUploading(true);
    try {
      for (const f of Array.from(files)) {
        if (f.size > 10 * 1024 * 1024) {
          toast.error(`"${f.name}" is over 10 MB`);
          continue;
        }
        const data_url = await readAsDataURL(f);
        await client.post(`/admin/customers/${customer.id}/documents`, {
          name: f.name,
          category,
          label,
          data_url,
          property_id: propertyId,
        });
      }
      toast.success("Documents uploaded");
      setLabel("");
      await load();
      refresh && refresh();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally { setUploading(false); }
  };

  const downloadDoc = async (d) => {
    const { data } = await client.get(`/admin/customer_documents/${d.id}/download`);
    const a = document.createElement("a");
    a.href = data.data_url;
    a.download = d.name;
    document.body.appendChild(a); a.click(); a.remove();
  };

  const removeDoc = async (d) => {
    if (!window.confirm(`Delete "${d.name}"?`)) return;
    await client.delete(`/admin/customer_documents/${d.id}`);
    toast.success("Document removed");
    await load();
    refresh && refresh();
  };

  return (
    <div>
      <div className="border border-[#E5E7EB] rounded-md p-4 bg-[#FAFAF7] mb-5">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] font-semibold mb-3">
          Upload new document
        </div>
        <div className="grid md:grid-cols-3 gap-3">
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Category</span>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="trent-input mt-1"
              data-testid="doc-category-select"
            >
              {categories.map((c) => <option key={c}>{c}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Property (optional)</span>
            <select
              value={propertyId}
              onChange={(e) => setPropertyId(e.target.value)}
              className="trent-input mt-1"
              data-testid="doc-property-select"
            >
              <option value="">Customer-only (no property)</option>
              {customerProps.map((p) => (
                <option key={p.id} value={p.id}>{p.address}</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="text-[10px] uppercase tracking-wider text-[#64748B] font-semibold">Custom label (optional)</span>
            <input
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. 2025 Renewal Lease"
              className="trent-input mt-1"
              data-testid="doc-label-input"
            />
          </label>
        </div>
        <label className="mt-4 inline-flex items-center gap-2 trent-btn-primary cursor-pointer" data-testid="doc-upload-btn">
          <UploadSimple size={14} weight="bold" /> {uploading ? "Uploading…" : "Choose files"}
          <input
            type="file"
            multiple
            className="hidden"
            onChange={(e) => onUpload(e.target.files)}
          />
        </label>
        <div className="text-[10px] text-[#94A3B8] mt-2">
          Up to 10 MB per file. Tagging to a property makes the document visible to every customer assigned to that property.
        </div>
      </div>

      {docs.length === 0 ? (
        <div className="text-sm text-[#64748B] border border-dashed border-[#E5E7EB] rounded-md p-6 text-center" data-testid="empty-documents">
          No documents uploaded for this customer yet.
        </div>
      ) : (
        <div className="border border-[#E5E7EB] rounded-md divide-y divide-[#E5E7EB]">
          {docs.map((d) => (
            <div key={d.id} className="p-3 flex items-center justify-between gap-3" data-testid={`doc-${d.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <FileText size={20} weight="duotone" className="text-[#1E3A5F] shrink-0" />
                <div className="min-w-0">
                  <div className="font-semibold truncate">{d.name}</div>
                  <div className="text-[11px] text-[#64748B]">
                    {d.category}{d.label ? ` · ${d.label}` : ""} · uploaded {d.created_at?.slice(0, 10)}
                  </div>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={() => downloadDoc(d)} className="p-2 hover:bg-[#F1F5F9] rounded-sm" title="Download" data-testid={`doc-download-${d.id}`}>
                  <DownloadSimple size={16} />
                </button>
                <button onClick={() => removeDoc(d)} className="p-2 hover:bg-[#FEE2E2] rounded-sm text-[#991B1B]" title="Delete" data-testid={`doc-remove-${d.id}`}>
                  <Trash size={16} />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const Field = ({ label, value }) => (
  <div>
    <div className="text-[10px] uppercase tracking-[0.25em] text-[#64748B] font-semibold">{label}</div>
    <div className="font-semibold text-[#0F172A] mt-1">{value}</div>
  </div>
);

function AssignInspectionModal({ inspection, customers, onClose }) {
  const [selected, setSelected] = useState(inspection.customer_user_ids || []);
  const [saving, setSaving] = useState(false);

  const toggle = (id) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const save = async () => {
    setSaving(true);
    try {
      await client.post(`/admin/inspections/${inspection.id}/assign_customers`, {
        customer_user_ids: selected,
      });
      toast.success("Customers updated");
      onClose();
    } catch { toast.error("Failed to save"); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="assign-modal">
      <div className="bg-white rounded-md w-full max-w-md max-h-[80vh] overflow-y-auto p-6">
        <h3 className="trent-heading text-lg font-bold text-[#0F172A] mb-1">Assign customers</h3>
        <p className="text-xs text-[#64748B] mb-4">Inspection {inspection.id.slice(0, 8)} · {inspection.inspection_type}</p>
        {customers.length === 0 && <div className="text-[#64748B] text-sm">No customers yet.</div>}
        <div className="space-y-2 mb-6">
          {customers.map((c) => (
            <label key={c.id} className="flex items-center gap-3 p-2 border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC] cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(c.id)}
                onChange={() => toggle(c.id)}
                data-testid={`assign-toggle-${c.id}`}
              />
              <div className="flex-1">
                <div className="font-semibold">{c.name}</div>
                <div className="text-xs text-[#64748B]">{c.email}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="trent-btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="trent-btn-primary" data-testid="assign-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

function AssignPropertiesModal({ customer, properties, onClose }) {
  const initial = properties
    .filter((p) => (p.customer_user_ids || []).includes(customer.id))
    .map((p) => p.id);
  const [selected, setSelected] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [filter, setFilter] = useState("");

  const toggle = (id) => {
    setSelected(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  };

  const save = async () => {
    setSaving(true);
    try {
      // Compute diff against the initial state — only call the API for props
      // whose membership actually changed for this customer.
      const initialSet = new Set(initial);
      const selectedSet = new Set(selected);
      const updates = properties
        .filter((p) =>
          (initialSet.has(p.id) && !selectedSet.has(p.id)) ||
          (!initialSet.has(p.id) && selectedSet.has(p.id))
        )
        .map((p) => {
          const existing = new Set(p.customer_user_ids || []);
          if (selectedSet.has(p.id)) existing.add(customer.id);
          else existing.delete(customer.id);
          return client.post(`/admin/properties/${p.id}/assign_customers`, {
            customer_user_ids: Array.from(existing),
          });
        });
      await Promise.all(updates);
      toast.success(`${selected.length} propert${selected.length === 1 ? "y" : "ies"} assigned`);
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const visible = properties.filter((p) =>
    !filter ||
    p.address?.toLowerCase().includes(filter.toLowerCase()) ||
    p.eircode?.toLowerCase().includes(filter.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" data-testid="assign-properties-modal">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[85vh] overflow-y-auto p-6">
        <h3 className="trent-heading text-lg font-bold text-[#0F172A] mb-1">Assign properties to {customer.name}</h3>
        <p className="text-xs text-[#64748B] mb-4">Customer will see selected properties in their portal.</p>

        <input
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          placeholder="Search address or Eircode…"
          className="trent-input mb-3"
          data-testid="assign-property-search"
        />

        {visible.length === 0 && <div className="text-[#64748B] text-sm">No properties.</div>}
        <div className="space-y-2 mb-6 max-h-[50vh] overflow-y-auto">
          {visible.map((p) => (
            <label key={p.id} className="flex items-center gap-3 p-2 border border-[#E5E7EB] rounded-md hover:bg-[#F8FAFC] cursor-pointer">
              <input
                type="checkbox"
                checked={selected.includes(p.id)}
                onChange={() => toggle(p.id)}
                data-testid={`assign-prop-toggle-${p.id}`}
              />
              <div className="flex-1 min-w-0">
                <div className="font-semibold truncate">{p.address}</div>
                <div className="text-xs text-[#64748B]">{p.eircode || "—"} · {p.property_type}</div>
              </div>
            </label>
          ))}
        </div>
        <div className="flex justify-end gap-2">
          <button onClick={onClose} className="trent-btn-secondary">Cancel</button>
          <button onClick={save} disabled={saving} className="trent-btn-primary" data-testid="assign-properties-save">
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
